import { useCallback, useEffect, useRef, useState } from "react";
import { rawApiFetch } from "../../api/client";
import type { HomeGreeting, WorkoutLogPill } from "../../api/types";
import type { Message, ProposalAnswer } from "./types";
import { decodeArtifacts } from "./artifacts/types";
import { consumeFirstRunPayoff } from "./firstRunPayoff";
import { foldsAway, greetingPath, readRecorded } from "./offers";

/**
 * The fallback opener when `/api/coach/home-greeting` is unreachable.
 *
 * Deliberately its own string rather than a copy of the backend's fallback
 * (api/routers/coach.py's `fallback` in `get_home_greeting`) — not because of
 * an em dash any more (Task 10 / R13b fixed that one), but because reaching
 * this branch means the API request itself failed or returned something this
 * client can't use, in which case sending a message will likely fail too. The
 * line's only job is to keep the transcript from opening blank; it has no
 * reason to match backend copy it never actually received.
 */
const OPENER_FALLBACK = "Ready when you are. What do you want to work on?";

export interface CoachSession {
  sessionId: string | null;
  setSessionId: React.Dispatch<React.SetStateAction<string | null>>;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  followups: (string | WorkoutLogPill)[];
  setFollowups: React.Dispatch<React.SetStateAction<(string | WorkoutLogPill)[]>>;
  isLoadingSession: boolean;
  isEphemeral: boolean;
  startNewChat: () => Promise<void>;
  /** Dismiss the trigger speaking in the opener and load the ordinary one.
   *  Resolves false when the server did not take it (PC-I54). */
  dismissOpener: (occurrenceId: number) => Promise<boolean>;
  /** Answer the lighter session the verdict's line offers (#270). */
  answerProposal: (occurrenceId: number, answer: ProposalAnswer) => Promise<boolean>;
  /** Answer what a later trigger's line offers (PC-I71). Resolves false
   *  only when nothing was saved and trying again can work (PC-I54). `kind`
   *  lets the answer fold the line away where the kind's decline does
   *  (PC-I75); without it the answer never does. */
  answerOffer: (occurrenceId: number, answer: string, kind?: string) => Promise<boolean>;
}

/**
 * Session lifecycle for the AI coach chat, lifted out of AICoach.tsx: fetch/
 * restore on mount, opener injection, and "start a new chat".
 *
 * Deliberately does NOT depend on useCoachStream — it never calls `stop()`.
 * The two hooks have a genuine circular dependency (useCoachStream's options
 * need `setMessages`/`setFollowups`, which live here), so the shell declares
 * this hook first and performs the "abort any in-flight stream, then reset
 * the session" sequence itself: `if (isStreaming) stop(); await
 * startNewChat();` — same two actions, same order as the original single
 * `startNewChat` that opened with `if (isStreaming) stop();`.
 *
 * `setSessionId` is exposed for the same reason `setMessages`/`setFollowups`
 * are (see their doc note below): `sendMessage` in the shell still assigns a
 * fallback session id itself (`sessionId ?? crypto.randomUUID()`) when none
 * exists yet, exactly as the pre-extraction component did.
 */
export function useCoachSession({ aiOff = false }: { aiOff?: boolean } = {}): CoachSession {
  // Read when the opener fails (below), never as a dependency: the opener is fetched on mount and on
  // "New", and a change of consent does not fetch it again.
  const aiOffRef = useRef(aiOff);
  aiOffRef.current = aiOff;
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionEphemeral, setSessionEphemeral] = useState(false);
  // Starts true: fetchSession() fires from an effect on mount, so the spinner
  // must already cover that first tick before the effect has even run.
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [followups, setFollowups] = useState<(string | WorkoutLogPill)[]>([]);

  // ── Opener helper ─────────────────────────────────────────────────────────

  /**
   * The coach's first message when a session is empty.
   *
   * One request, not two: the backend writes the prose *and* attaches the Up
   * Next card (design decision D10 — deterministic, so spending a model tool
   * round on a card that is the same every time buys nothing and costs
   * latency). `data.artifacts` still goes through `decodeArtifacts` rather
   * than a bare cast — it is the same envelope shape a chat turn's artifacts
   * arrive in, and this is the one gate (R5b) that lets an unknown type or
   * version land here without crashing the renderer.
   *
   * `data.text` gets the same tolerance treatment as a sibling field in the
   * same payload: a 200 whose body parses as JSON but doesn't carry a
   * non-blank `text` (a stale frontend talking to a redeployed backend that
   * changed the shape, a proxy/mock returning `{}`, a whitespace-only string,
   * etc.) is thrown into the same catch below rather than trusted blindly —
   * `res.ok` and `res.json()` both succeed in that case, so without this
   * check the opener would render a blank assistant bubble instead of
   * falling back. The `.trim()` matters: `" "` passes a bare length check
   * but still renders as an empty-looking bubble.
   *
   * The fallback is short and deliberately its own string rather than a copy
   * of the backend's own fallback (see OPENER_FALLBACK's doc comment above
   * for why — it is no longer about an em dash, Task 10 / R13b fixed that).
   * Reaching it means the API is unreachable or returning something this
   * client can't use, in which case sending a message will likely fail too,
   * so the line's job is only to keep the transcript from opening blank.
   *
   * `consumeFirstRunPayoff()` (Task 10, FR-D6): appends `?first_run=1`
   * exactly once — on whichever call reaches here first after Layout armed
   * the flag from FirstRun.tsx's `navigate('/', { state: { firstRun: true
   * } })`. The flag is consumed (cleared) the moment it's read, so a second
   * Coach visit or a `startNewChat` in the same page load gets the ordinary
   * opener, and a page reload never sees it at all — see
   * components/coach/firstRunPayoff.ts's doc comment for why a module-level
   * flag, not React state or sessionStorage, is what makes both of those
   * true for free.
   */
  const injectOpener = useCallback(async () => {
    try {
      const firstRun = consumeFirstRunPayoff();
      // The offer kinds this client renders ride on every request (PC-I71):
      // a line with an offer this page cannot answer is never sent to it.
      const res = await rawApiFetch(greetingPath({ firstRun }));
      if (!res.ok) throw new Error(`home-greeting ${res.status}`);
      const data = (await res.json()) as HomeGreeting;
      if (typeof data.text !== "string" || data.text.trim().length === 0) {
        throw new Error("home-greeting response missing text");
      }
      const trigger = data.trigger;
      const dismissable =
        trigger != null && trigger.dismissable && Number.isInteger(trigger.occurrence_id);
      setMessages([
        {
          role: "assistant",
          content: data.text,
          artifacts: decodeArtifacts(data.artifacts),
          pending: false,
          // Only a trigger that says it can be dismissed gets the control;
          // a malformed id is treated as no trigger rather than a dead button.
          openerTrigger: dismissable ? { occurrenceId: trigger.occurrence_id } : null,
          // The verdict's reasons and its lighter session (#270) travel with
          // its line, and only with a trigger this client can answer for.
          openerVerdict: dismissable ? (data.verdict ?? null) : null,
          openerProposal: dismissable ? (data.proposal ?? null) : null,
          // Every later trigger's offer (PC-I71), on the same terms.
          openerOffer: dismissable ? (data.offer ?? null) : null,
        },
      ]);
    } catch (err) {
      console.error("[useCoachSession] opener failed:", err);
      // The fallback invites a message ("What do you want to work on?"), and without the AI consent the
      // composer refuses one (CS-D19, board finding F8): a line that asks for nothing is no line at all.
      // The server's own opener for this case is written by code and asks for nothing; this is only the
      // page's answer to not having reached it.
      setMessages(aiOffRef.current ? [] : [{ role: "assistant", content: OPENER_FALLBACK, pending: false }]);
    }
  }, []);

  /**
   * "Got it" on a trigger's opener (#269, PC-I7): record the dismissal on the
   * server -- so every surface and device agrees -- then fetch the opener
   * again, which is now the ordinary greeting, written without the finding
   * (PC-I3). Only while the opener is the whole transcript: once the user has
   * replied it is part of the conversation, and the shell stops offering it.
   *
   * A failed dismissal leaves the line where it is; nothing is hidden locally
   * that the server still thinks was never dismissed. It resolves false, so
   * the controls can say so (PC-I54).
   *
   * Between the two, the bubble shows the coach thinking, as it does before
   * any reply: the ordinary greeting may be written by the model, and an
   * instant swap of the text the user is reading, seconds after they
   * clicked, reads as nothing having happened and then something jumping.
   * The thinking state reuses the bubble's own exit and entrance, so this
   * adds no motion of its own.
   */
  const dismissOpener = useCallback(
    async (occurrenceId: number): Promise<boolean> => {
      try {
        const res = await rawApiFetch(`/api/coach/opener/${occurrenceId}/dismiss`, {
          method: "POST",
        });
        if (!res.ok && res.status !== 404) throw new Error(`dismiss ${res.status}`);
      } catch (err) {
        console.error("[useCoachSession] dismiss failed:", err);
        return false;
      }
      setMessages([{ role: "assistant", content: "", pending: true }]);
      await injectOpener();
      return true;
    },
    [injectOpener],
  );

  /**
   * An answer to the lighter session the verdict's line offers (#270): "Ease
   * my next session" or "I'll go lighter" (accepted), "Keep my plan"
   * (declined) or the record line's "Undo" (withdrawn).
   *
   * Written to the server first, and nothing changes here unless it took the
   * answer (PC-I42): a failed request leaves the controls where they are,
   * with no record line claiming what did not happen. A 409 means the line
   * is out of date (a session has been logged since, or has started under
   * the call), and no answer can change that, so the opener loads again, as
   * a dismissal's 404 does (PC-I61). "Keep my plan" is the decline and the
   * dismissal in one (PC-I41), so it folds the line away the way "Got it"
   * does, through the same thinking state. Resolves false only when nothing
   * was saved and trying again can work (PC-I54).
   */
  const answerProposal = useCallback(
    async (occurrenceId: number, answer: ProposalAnswer): Promise<boolean> => {
      try {
        const res = await rawApiFetch(`/api/coach/opener/${occurrenceId}/proposal`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answer }),
        });
        if (res.status === 409) {
          setMessages([{ role: "assistant", content: "", pending: true }]);
          await injectOpener();
          return true;
        }
        if (!res.ok) throw new Error(`proposal ${res.status}`);
      } catch (err) {
        console.error("[useCoachSession] proposal answer failed:", err);
        return false;
      }
      if (answer === "declined") {
        setMessages([{ role: "assistant", content: "", pending: true }]);
        await injectOpener();
        return true;
      }
      setMessages((prev) =>
        prev.map((m, i) =>
          i === 0 && m.openerProposal
            ? {
                ...m,
                openerProposal: {
                  ...m.openerProposal,
                  answer: answer === "accepted" ? "accepted" : null,
                },
              }
            : m,
        ),
      );
      return true;
    },
    [injectOpener],
  );

  /**
   * An answer to what a later trigger's line offers (PC-I71), on #270's
   * terms: written to the server first, and nothing changes here unless it
   * took the answer. A 409 means the line is out of date (its answer can no
   * longer change, or the kind finds the offer has moved on), and no answer
   * can change that, so the opener loads again with no note (PC-I61). The
   * kinds' own answers are the server's table; `withdrawn` is the record
   * line's Undo, and the offer returns unanswered.
   *
   * The record line is drawn from what the server recorded (PC-I118), its
   * response, never from the answer sent here: an add kind's yes may be
   * recorded as `already_there`, and "Added to {template}." must not follow
   * a yes that wrote nothing. A response that is not the promised shape is
   * treated as a stale line and the opener loads again, so what shows is the
   * server's own record. So does an answer that folds the line away
   * (`kind`'s decline, PC-I75): the opener loads again, as it does for a
   * dismissal; without `kind` no answer does.
   */
  const answerOffer = useCallback(
    async (occurrenceId: number, answer: string, kind?: string): Promise<boolean> => {
      let body: unknown = null;
      try {
        const res = await rawApiFetch(`/api/coach/opener/${occurrenceId}/offer`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answer }),
        });
        if (res.status === 409) {
          setMessages([{ role: "assistant", content: "", pending: true }]);
          await injectOpener();
          return true;
        }
        if (!res.ok) throw new Error(`offer ${res.status}`);
        body = await res.json().catch(() => null);
      } catch (err) {
        console.error("[useCoachSession] offer answer failed:", err);
        return false;
      }
      const recorded = readRecorded(body);
      if (recorded === null || foldsAway(kind, recorded.answer)) {
        setMessages([{ role: "assistant", content: "", pending: true }]);
        await injectOpener();
        return true;
      }
      // Onto the offer as it is now: a reload or the x can replace the opener
      // while the request is in flight, and the copy from before the await
      // would bring the old line back.
      setMessages((prev) =>
        prev.map((m, i) =>
          i === 0 && m.openerOffer?.occurrence_id === occurrenceId
            ? { ...m, openerOffer: { ...m.openerOffer, ...recorded } }
            : m,
        ),
      );
      return true;
    },
    [injectOpener],
  );

  // ── Fetch/restore session ─────────────────────────────────────────────────

  const fetchSession = useCallback(async () => {
    setIsLoadingSession(true);
    try {
      const res = await rawApiFetch("/api/coach/session");
      if (!res.ok) {
        console.error("[useCoachSession] GET /api/coach/session failed:", res.status);
        setSessionId(crypto.randomUUID());
        await injectOpener();
        return;
      }
      const data = (await res.json()) as {
        session_id: string;
        messages: Array<{
          role: "user" | "assistant";
          content: string;
          followups?: (string | WorkoutLogPill)[];
          // Sent by the backend today (api/schemas/coach.py). Typed
          // `unknown` because decodeArtifacts is the only thing allowed to
          // decide what it is.
          artifacts?: unknown;
        }>;
        ephemeral?: boolean;
      };
      setSessionId(data.session_id);
      setSessionEphemeral(!!data.ephemeral);
      const restored = data.messages ?? [];
      if (restored.length === 0) {
        await injectOpener();
      } else {
        setMessages(
          restored.map((m) => ({
            ...m,
            pending: false,
            // Every assistant turn, not just the last: artifacts are part of
            // the transcript, where followups are not (design spec §3.5).
            artifacts: decodeArtifacts(m.artifacts),
          }))
        );
        const lastMsg = restored[restored.length - 1];
        if (lastMsg?.role === "assistant" && lastMsg.followups?.length) {
          setFollowups(lastMsg.followups as (string | WorkoutLogPill)[]);
        } else {
          setFollowups([]);
        }
      }
    } catch (err) {
      console.error("[useCoachSession] fetchSession error:", err);
      setSessionId(crypto.randomUUID());
      await injectOpener();
    } finally {
      setIsLoadingSession(false);
    }
  }, [injectOpener]);

  // ── New chat ──────────────────────────────────────────────────────────────
  // No `if (isStreaming) stop()` here — that line moved to the shell's
  // new-chat handler, which calls `stop()` before this, to break the circular
  // dependency on useCoachStream. See the module doc comment above.

  const startNewChat = useCallback(async () => {
    setIsLoadingSession(true);
    try {
      const res = await rawApiFetch("/api/coach/session/new", { method: "POST" });
      if (!res.ok) {
        console.error("[useCoachSession] POST /api/coach/session/new failed:", res.status);
        setSessionId(crypto.randomUUID());
      } else {
        const data = (await res.json()) as {
          session_id: string;
          messages: Message[];
          ephemeral?: boolean;
        };
        setSessionId(data.session_id);
        setSessionEphemeral(!!data.ephemeral);
      }
      await injectOpener();
    } catch (err) {
      console.error("[useCoachSession] startNewChat error:", err);
      setSessionId(crypto.randomUUID());
      await injectOpener();
    } finally {
      setIsLoadingSession(false);
      setFollowups([]);
    }
  }, [injectOpener]);

  // ── Lifecycle: fetch/restore on mount ───────────────────────────────────────
  // The coach is a route now, mounted exactly when it's open, and the opener
  // comes from the backend (which reads coach_language itself) rather than a
  // client-side pick — so this no longer waits on a profile query either.

  useEffect(() => {
    void fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  return {
    sessionId,
    setSessionId,
    messages,
    setMessages,
    followups,
    setFollowups,
    isLoadingSession,
    isEphemeral: sessionEphemeral,
    startNewChat,
    dismissOpener,
    answerProposal,
    answerOffer,
  };
}
